import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import type { FormatNumber } from "$root/localization/formatters";
import { labSub } from "$root/functional-areas/portfolio-lab/utils/math";
import type { ConceptDefinitionDTO, DataResponseDTO, ParameterizedConceptDTO } from "@eu.mdotm.api/smart-workspaces";
import { Row, Text } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import type { ColDef, ColumnState } from "ag-grid-community";
import type { CustomCellRendererProps } from "ag-grid-react";

/** colId of the pinned, non-concept name column. Excluded from the persisted column state / `series`. */
export const NAME_COL_ID = "name";

/** One grid row per lab investment; `metricValues` is keyed by parametrized-concept uuid. */
export type ConceptMetricsRow = {
	uuid: string;
	name: string;
	color: string;
	metricValues: Partial<Record<string, number | null>>;
	/** Baseline (pre-edit) values keyed by parametrized-concept uuid — when present, cells render a colored delta. */
	baselineMetricValues?: Partial<Record<string, number | null>>;
};

/**
 * Three-state delta of a metric cell vs its baseline: a computed zero must render distinctly from
 * a missing baseline, so "no delta chunk" never masquerades as "no change".
 */
export type ConceptMetricDelta = { kind: "delta"; displayDelta: number } | { kind: "zero" } | { kind: "absent" };

/**
 * Delta of a metric cell vs its baseline, in DISPLAY units (percentage metrics ×100).
 * `zero` when both sides are present but the delta rounds to zero at the displayed decimal
 * places; `absent` when the value or baseline is missing, or the delta is NaN (not a computed
 * value).
 */
export function conceptMetricDisplayDelta(opts: {
	value: number | null | undefined;
	baseline: number | null | undefined;
	isPercentage: boolean;
	decimalPlaces: number;
}): ConceptMetricDelta {
	const { value, baseline, isPercentage, decimalPlaces } = opts;
	if (value == null || baseline == null) {
		return { kind: "absent" };
	}
	const delta = labSub(value, baseline);
	const displayDelta = isPercentage ? delta * 100 : delta;
	if (Number.isNaN(displayDelta)) {
		return { kind: "absent" };
	}
	if (Math.round(Math.abs(displayDelta) * 10 ** decimalPlaces) === 0) {
		return { kind: "zero" };
	}
	return { kind: "delta", displayDelta };
}

/**
 * Delta chunk appended to a metric cell, one rendering per {@link ConceptMetricDelta} state:
 * signed colored "(+1.29%)" (green/red, same palette as the composition editor's Δ column),
 * unsigned muted "(0.00%)" for a computed zero, muted "(—)" for a missing baseline. Pair with
 * {@link conceptMetricDisplayDelta}, which owns the scaling and state classification.
 */
export function MetricDeltaIndicator({
	delta,
	isPercentage,
	decimalPlaces,
	formatNumber,
}: {
	delta: ConceptMetricDelta;
	isPercentage: boolean;
	decimalPlaces: number;
	formatNumber: FormatNumber;
}) {
	if (delta.kind === "absent") {
		return (
			<span
				data-qualifier="ConceptMetrics/DeltaIndicator"
				data-delta-kind="absent"
				style={{ color: themeCSSVars.palette_N500, fontWeight: 400 }}
			>
				(—)
			</span>
		);
	}
	if (delta.kind === "zero") {
		return (
			<span
				data-qualifier="ConceptMetrics/DeltaIndicator"
				data-delta-kind="zero"
				style={{ color: themeCSSVars.palette_N500, fontWeight: 400 }}
			>
				({formatNumber(0, decimalPlaces)}
				{isPercentage ? "%" : ""})
			</span>
		);
	}
	const sign = delta.displayDelta > 0 ? "+" : "−";
	return (
		<span
			data-qualifier="ConceptMetrics/DeltaIndicator"
			data-delta-kind={delta.displayDelta > 0 ? "positive" : "negative"}
			style={{
				color: delta.displayDelta > 0 ? themeCSSVars.palette_P600 : themeCSSVars.palette_D500,
				fontWeight: 400,
			}}
		>
			({sign}
			{formatNumber(Math.abs(delta.displayDelta), decimalPlaces)}
			{isPercentage ? "%" : ""})
		</span>
	);
}

export const nameColDef: ColDef<ConceptMetricsRow> = {
	colId: NAME_COL_ID,
	field: "name",
	headerName: "Name",
	pinned: "left" as const,
	minWidth: 160,
	lockVisible: true,
	lockPinned: true,
	suppressMovable: true,
	cellRenderer(params: CustomCellRendererProps<ConceptMetricsRow>) {
		return (
			<Row className="w-full h-full" alignItems="center" gap={8} data-column-color={params.data?.color}>
				<ColoredRectangle variant="vertical" color={params.data?.color ?? ""} />
				<Text type="Body/M/Book">{params.data?.name}</Text>
			</Row>
		);
	},
};

/**
 * Builds the concept columns of a metrics grid: one column per parametrized-concept uuid (in the
 * given order), preceded by the pinned name column. Header and value formatting derive from the
 * parametrized concept and its concept definition (label, `unitOfMeasurement === "%"`,
 * `numericDecimalPlaces`); visibility comes from the matching `ColumnState`, when provided.
 *
 * With `showDelta`, cells render a delta chunk vs `baselineMetricValues` in one of three states:
 * "3.79% (+1.29%)" colored green/red by sign (same palette as the composition editor's Δ column),
 * muted "(0.00%)" for a delta that rounds to zero at display precision, muted "(—)" when the
 * value or baseline is missing. Sorting/copy still use the plain current value.
 */
export function buildConceptMetricColumnDefs(opts: {
	orderedMetricUuids: string[];
	parametrizedConcepts: ParameterizedConceptDTO[];
	conceptDefsById: Map<string, ConceptDefinitionDTO>;
	columnStates?: ColumnState[];
	formatNumber: FormatNumber;
	showDelta?: boolean;
	/** Stem of each value cell's `data-qualifier`, which reads `<stem>(<row uuid>,<conceptId>)`. */
	valueQualifier?: string;
}): ColDef<ConceptMetricsRow>[] {
	const {
		orderedMetricUuids,
		parametrizedConcepts,
		conceptDefsById,
		columnStates = [],
		formatNumber,
		showDelta = false,
		valueQualifier,
	} = opts;
	return [
		nameColDef,
		...orderedMetricUuids.map((uuid): ColDef<ConceptMetricsRow> => {
			const pc = parametrizedConcepts.find((c) => c.uuid === uuid);
			const def = pc ? conceptDefsById.get(pc.conceptId) : undefined;
			const isPercentage = def?.unitOfMeasurement === "%";
			const colState = columnStates.find((c) => c.colId === uuid);
			const decimalPlaces = def?.numericDecimalPlaces ?? 2;
			const formatValue = (val: number | null): string => {
				if (val == null) {
					return "-";
				}
				if (isPercentage) {
					return `${formatNumber(val * 100, decimalPlaces)}%`;
				}
				return formatNumber(val, decimalPlaces);
			};
			return {
				colId: uuid,
				headerName: pc?.label ?? def?.defaultLabel ?? uuid,
				hide: colState != null ? (colState.hide ?? false) : false,
				valueGetter: (params) => params.data?.metricValues[uuid] ?? null,
				valueFormatter: (params) => formatValue(params.value as number | null),
				cellDataType: "number",
				...((showDelta || valueQualifier != null) && {
					cellRenderer: (params: CustomCellRendererProps<ConceptMetricsRow>) => {
						const val = params.value as number | null;
						const delta = conceptMetricDisplayDelta({
							value: val,
							baseline: params.data?.baselineMetricValues?.[uuid],
							isPercentage,
							decimalPlaces,
						});
						return (
							<span
								data-qualifier={
									valueQualifier != null
										? `${valueQualifier}(${params.data?.uuid ?? ""},${pc?.conceptId ?? uuid})`
										: undefined
								}
							>
								{formatValue(val)}
								{showDelta && (
									<>
										{" "}
										<MetricDeltaIndicator
											delta={delta}
											isPercentage={isPercentage}
											decimalPlaces={decimalPlaces}
											formatNumber={formatNumber}
										/>
									</>
								)}
							</span>
						);
					},
				}),
			};
		}),
	];
}

/** Extracts one row's metric values from a data-provider response, keyed by parametrized-concept uuid. */
export function extractConceptMetricValues(
	result: DataResponseDTO | undefined,
	concepts: ParameterizedConceptDTO[],
): Partial<Record<string, number | null>> {
	return Object.fromEntries(
		concepts.map((pc) => {
			const raw = result?.items?.[0]?.dictionary?.[pc.uuid!]?.value;
			return [pc.uuid!, raw != null ? (raw as unknown as number) : null];
		}),
	);
}
